import React from 'react'
import { SCENE, type TimeOfDay } from '../lib/timeOfDay'

// Flat-illustration nature scene for GreetingPanel's header banner — layered sky,
// sun/moon, far mountains, rolling hills and firs, one variant per time of day.
// Inline SVG, no assets. Rendered full-bleed inside a fixed-height banner; the
// panel body below it is solid, so cards never sit on the artwork.

const STARS: Array<[number, number, number]> = [
  [54, 28, 1.6], [126, 52, 1.1], [206, 24, 1.9], [286, 44, 1.1], [356, 30, 1.5],
  [436, 56, 1.1], [506, 32, 1.8], [238, 70, 0.9], [108, 74, 1.1], [566, 62, 1.1],
  [664, 34, 1.2], [716, 70, 0.9], [408, 72, 1.4], [176, 40, 1],
]

const FIR = 'M0,-18 L-7,-6 L-3,-6 L-8,4 L8,4 L3,-6 L7,-6 Z'
const FIRS: Array<[number, number]> = [[80, 134], [120, 138], [158, 134], [490, 132], [532, 137], [576, 132]]

const ORB: Record<TimeOfDay, { cx: number; cy: number; r: number; glow: number }> = {
  morning: { cx: 610, cy: 100, r: 24, glow: 90 },
  afternoon: { cx: 652, cy: 52, r: 24, glow: 96 },
  evening: { cx: 400, cy: 112, r: 32, glow: 155 },
  night: { cx: 652, cy: 50, r: 18, glow: 66 },
}

const Cloud: React.FC<{ x: number; y: number; s: number; o: number }> = ({ x, y, s, o }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} fill="#FFFFFF" opacity={o}>
    <ellipse cx="0" cy="7" rx="28" ry="12" />
    <ellipse cx="20" cy="1" rx="18" ry="14" />
    <ellipse cx="-18" cy="4" rx="15" ry="11" />
  </g>
)

export const GreetingBackdrop: React.FC<{ period: TimeOfDay }> = ({ period }) => {
  const c = SCENE[period]
  const skyId = `gb-sky-${period}`
  const glowId = `gb-glow-${period}`
  const o = ORB[period]

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 800 150"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={skyId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c.skyFrom} />
          {c.skyMid && <stop offset="55%" stopColor={c.skyMid} />}
          <stop offset="100%" stopColor={c.skyTo} />
        </linearGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={c.orbGlow} stopOpacity="0.9" />
          <stop offset="100%" stopColor={c.orbGlow} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="800" height="150" fill={`url(#${skyId})`} />

      {period === 'night' &&
        STARS.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} fill={c.star} opacity={0.9} />)}

      <circle cx={o.cx} cy={o.cy} r={o.glow} fill={`url(#${glowId})`} />
      {period === 'night' ? (
        <>
          <circle cx={o.cx} cy={o.cy} r={o.r} fill={c.orb} />
          <circle cx={o.cx - 7} cy={o.cy - 5} r={o.r - 2.5} fill={`url(#${skyId})`} />
        </>
      ) : (
        <circle cx={o.cx} cy={o.cy} r={o.r} fill={c.orb} />
      )}

      {period === 'morning' && (
        <>
          <Cloud x={155} y={46} s={0.75} o={0.6} />
          <Cloud x={440} y={32} s={0.55} o={0.45} />
        </>
      )}
      {period === 'afternoon' && (
        <>
          <Cloud x={220} y={54} s={0.65} o={0.55} />
          <Cloud x={510} y={36} s={0.46} o={0.4} />
        </>
      )}

      {/* far mountains */}
      <path
        d="M0,108 L60,68 L110,92 L170,60 L230,90 L300,62 L370,98 L440,60 L510,90 L580,62 L660,96 L730,66 L800,90 L800,150 L0,150 Z"
        fill={c.hillFar}
        opacity={0.6}
      />
      {/* rolling hills */}
      <path
        d="M0,122 C90,100 180,128 300,112 C420,96 500,126 620,110 C690,101 750,118 800,110 L800,150 L0,150 Z"
        fill={c.hillMid}
      />
      <path
        d="M0,136 C130,116 250,144 380,126 C500,110 620,140 800,124 L800,150 L0,150 Z"
        fill={c.hillNear}
      />
      {FIRS.map(([x, y], i) => (
        <path key={i} d={FIR} transform={`translate(${x} ${y})`} fill={c.tree} />
      ))}
    </svg>
  )
}

export default React.memo(GreetingBackdrop)
